import { useMemo } from 'react';
import { accountApi } from '@netiva/classifieds-api';

import { AdStep } from '@/store/ad/types';
import { useAdParams } from './useAdParams';
import { useAdPublishing } from './useAdPublishing';
import { useAppSelector } from '@/store';
import { CommonStepKeys, CommonSteps } from '../constants';

export const useAdSteps = () => {
  const { categoryId, dataObjectType } = useAppSelector((state) => state.ad);
  const { currentStepKey } = useAdParams();

  const { data: stepsData, isLoading } = accountApi.useGetConfigurationProcess(
    {
      categoryId: categoryId,
      dataObjectType: dataObjectType,
    },
    { skip: !categoryId || !dataObjectType }
  );

  const {
    isLoading: isLoadingPublication,
    activePublication,
    availableContracts,
    availableProducts,
    selectedContract,
    selectedProduct,
  } = useAdPublishing();

  // a lone product without extensions leaves nothing to choose; the store preselects it
  const skipPublishing =
    !activePublication &&
    availableContracts.length === 0 &&
    availableProducts.length === 1 &&
    availableProducts[0].extensionProducts.length === 0;
  const requiresVerification =
    !isLoadingPublication &&
    !!(activePublication?.contract.product ?? selectedContract?.product ?? selectedProduct)?.requiresVerification;

  const steps = useMemo<AdStep[]>(() => {
    const isSkipped = (step: AdStep) =>
      // never drop the step the user is on, e.g. after following a direct link to it
      step.key !== currentStepKey &&
      ((step.key === CommonStepKeys.publishing && skipPublishing) ||
        (step.key === CommonStepKeys.verificationDocuments && !requiresVerification));

    return [...CommonSteps, ...(stepsData?.steps || []).filter((step) => !isSkipped(step))];
  }, [currentStepKey, requiresVerification, skipPublishing, stepsData?.steps]);
  const currentStepIndex = useMemo(
    () => steps.findIndex((step) => step.key === currentStepKey),
    [currentStepKey, steps]
  );
  const prevStepIndex = useMemo<number>(() => (currentStepIndex > 0 ? currentStepIndex - 1 : -1), [currentStepIndex]);

  const currentStep = useMemo<AdStep | undefined>(() => steps[currentStepIndex], [steps, currentStepIndex]);
  const prevStep = useMemo<AdStep | undefined>(
    () => (prevStepIndex >= 0 ? steps[prevStepIndex] : undefined),
    [steps, prevStepIndex]
  );

  return { currentStep, currentStepIndex, prevStep, isLoading, steps };
};
